import { ReactQuestionFactory } from "survey-react-ui";
import { Select } from "../ui";
import {
  ChangeEvent,
  createElement,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { QuestionDropdownModel, RendererFactory } from "survey-core";
import Textarea from "../ui/textarea";
import {
  mergeOptionsWithStorage,
  saveOptionToStorage,
  normalizeQuestionChoices,
  createSelectFilterFunction,
} from "../../utils/select-utils";
import { Option } from "../../types/select";

interface SelectQuestionProps {
  question: QuestionDropdownModel;
  isDisplayMode: boolean;
}

const SelectQuestion = ({ question, isDisplayMode }: SelectQuestionProps) => {
  const [comment, setComment] = useState(question.comment);
  const [options, setOptions] = useState<Option[]>(() => {
    const schemaOptions = normalizeQuestionChoices(question.visibleChoices);
    return mergeOptionsWithStorage(
      schemaOptions,
      question.getPropertyValue("localStorageKey", question.getValueName())
    );
  });
  const localStorageKey = question.getPropertyValue(
    "localStorageKey",
    question.getValueName()
  );
  // Keep options in sync with SurveyJS visibleChoices using property change subscription
  useEffect(() => {
    const recomputeOptions = () => {
      const schemaOptions = normalizeQuestionChoices(question.visibleChoices);
      const mergedOptions = mergeOptionsWithStorage(
        schemaOptions,
        localStorageKey
      );
      setOptions(mergedOptions);
    };

    // Initial compute
    recomputeOptions();

    // Subscribe to SurveyJS changes for visibleChoices
    const subscriptionKey = `visibleChoices:${question.getValueName()}`;
    const handler = () => {
      recomputeOptions();
    };
    question.registerPropertyChangedHandlers(
      ["visibleChoices"],
      handler,
      subscriptionKey
    );

    return () => {
      question.unregisterPropertyChangedHandlers(
        ["visibleChoices"],
        subscriptionKey
      );
    };
  }, [question, localStorageKey]);

  // // Listen to cross-tab localStorage updates for this storageKey
  useEffect(() => {
    if (!localStorageKey || typeof window === "undefined") return;

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== localStorageKey) return;
      const schemaOptions = normalizeQuestionChoices(question.visibleChoices);
      const mergedOptions = mergeOptionsWithStorage(
        schemaOptions,
        localStorageKey
      );
      setOptions(mergedOptions);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [localStorageKey, question]);

  // // Listen for same-tab updates via a custom event and refresh options
  useEffect(() => {
    if (!localStorageKey || typeof window === "undefined") return;

    const handleCustom = (event: any) => {
      if (event?.detail?.storageKey !== localStorageKey) return;
      const schemaOptions = normalizeQuestionChoices(question.visibleChoices);
      const mergedOptions = mergeOptionsWithStorage(
        schemaOptions,
        localStorageKey
      );
      setOptions(mergedOptions);
    };

    window.addEventListener("m2f:select-options-updated", handleCustom as any);
    return () =>
      window.removeEventListener(
        "m2f:select-options-updated",
        handleCustom as any
      );
  }, [localStorageKey, question]);

  const defaultValue = useMemo(() => {
    return question.defaultValue
      ? options.filter((o: Option) => o.value === question.defaultValue)
      : undefined;
  }, [question.defaultValue, options]);

  const selectedOption = useMemo(() => {
    return options.filter((o: Option) => o.value === question.value);
  }, [question.value, options]);

  const noOptionsMessage = useCallback(() => {
    return question.getPropertyValue(
      "noOptionsMessage",
      "%-%tolgee:select-question-no-options-default-message%-%"
    );
  }, [question]);

  const onSelectChange = useCallback(
    (selectedOption: any) => {
      if (!selectedOption) {
        question.value = undefined;
      } else {
        question.value = selectedOption.value;
      }
    },
    [question]
  );

  const onCommentChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setComment(event.target.value);
    },
    []
  );

  const onCommentBlur = useCallback(() => {
    question.comment = comment;
  }, [question, comment]);

  const onCreateOption = useCallback(
    (inputValue: string) => {
      // Persist in localStorage and notify other components
      saveOptionToStorage(inputValue, localStorageKey);

      // Set the selected value
      question.value = inputValue;
    },
    [question, localStorageKey]
  );

  const formatCreateLabel = useCallback(
    (inputValue: string) => {
      return question.getPropertyValue(
        "createOptionMessage",
        `%-%tolgee:select-question-create-option-default-message:input:${inputValue}%-%`
      );
    },
    [question]
  );

  return (
    <>
      <Select
        isCreatable={question.allowCustomChoices}
        isDisabled={question.isReadOnly || isDisplayMode}
        id={question.inputId}
        defaultValue={defaultValue}
        value={selectedOption}
        onChange={onSelectChange}
        options={options}
        required={question.isRequired}
        menuPortalTarget={
          typeof document !== "undefined"
            ? document.querySelector("body")
            : undefined
        }
        placeholder={question.placeholder}
        isClearable={question.allowClear}
        isSearchable={question.searchEnabled}
        noOptionsMessage={noOptionsMessage}
        hideSelectedOptions={false}
        onCreateOption={onCreateOption}
        formatCreateLabel={formatCreateLabel}
        filterOption={createSelectFilterFunction(options)}
      />
      {question.isOtherSelected && (
        <div className={question.cssClasses.commentArea}>
          <Textarea
            disabled={question.isReadOnly || question.isDisplayMode}
            onChange={onCommentChange}
            onBlur={onCommentBlur}
            value={comment}
            placeholder={question.commentPlaceHolder}
          />
        </div>
      )}
    </>
  );
};

ReactQuestionFactory.Instance.registerQuestion(
  "sv-select",
  function (props: any) {
    return createElement(SelectQuestion, props);
  }
);

RendererFactory.Instance.registerRenderer("dropdown", "select", "sv-select");
